import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal } from 'antd';
import * as yup from 'yup';

import { InputForm } from '@/components/form/Input';
import { SelectForm } from '@/components/form/Select';
import { LoadingStatus } from '@/core/enums/loadingStatus';
import { SystemType } from '@/core/enums/systemType';
import { useAppDispatch, useAppSelector } from '@/core/redux/hooks';
import { appSettingsActions } from '@/core/redux/slices/app/slice';
import { equipmentRequestSelectors } from '@/core/redux/slices/equipment/selectors';
import { equipmentRequestActions } from '@/core/redux/slices/equipment/slice';
import { systemSelectors } from '@/core/redux/slices/smokeExtraction/system/selectors';
import { systemActions } from '@/core/redux/slices/smokeExtraction/system/slice';

import { ModalNewProject } from '../tools/ToolsPage/ModalNewProject';

interface IAddNewProject {
  name: string;
  calculation_id: number;
  description?: string | null;
}

interface IModal {
  openModal?: boolean;
  setOpenModal: (value: boolean) => void;
}

export const CreatorOfTable: React.FC<IModal> = ({
  openModal,
  setOpenModal,
}) => {
  const [isSending, setSending] = useState<boolean>(false);
  const [openProjectModal, setOpenProjectModal] = useState<boolean>(false);

  const projectList = useAppSelector(equipmentRequestSelectors.projectList);
  const addSystemLock = useAppSelector(systemSelectors.addSystemLock);

  const selectOptions = [{ value: 0, label: 'Создать новый' }];

  projectList?.items?.forEach((el: any) => {
    selectOptions.unshift({ value: el?.id, label: el?.calc_name });
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      equipmentRequestActions.fetchProjectList({
        size: 100,
        page: 1,
      })
    );

    return () => {
      dispatch(
        systemActions.setAddSystemLock({
          status: LoadingStatus.NEVER,
          reponse: null,
        })
      );
    };
  }, []);

  useEffect(() => {
    const { status, reponse, error } = addSystemLock;

    if (status === LoadingStatus.LOADED && reponse) {
      systemActions.setUpdateSystemLock(LoadingStatus.NEVER);
      dispatch(
        appSettingsActions.setAppMessage({
          type: 'success',
          content: 'Данные успешно сохранены',
        })
      );

      navigate(`/air-exchange-table/${reponse.id}`);
    } else if (
      status === LoadingStatus.ERROR &&
      error?.message === 'Действие недоступно с текущим планом подписки'
    ) {
      setOpenModal(false);
    }
  }, [addSystemLock]);

  const schema = yup.object().shape({
    name: yup.string().required('Это поле обязательно для заполнения'),
    calculation_id: yup
      .number()
      .required('Это поле обязательно для заполнения'),
  });

  const methods = useForm<IAddNewProject>({
    resolver: yupResolver(schema),
  });

  const submit = () => {
    setSending(true);
    const methodsData = methods.getValues();
    dispatch(
      systemActions.addSystem({
        calculation_id: methodsData?.calculation_id,
        name: methodsData.name,
        type: SystemType.AirExchangeTable,
      })
    );
  };

  const handleSelectChange = (value: any) => {
    value !== 0
      ? methods.setValue('calculation_id', value)
      : setOpenProjectModal(true);
  };

  const getSelectLabel = (id: number) => {
    const result = projectList?.items?.find((el: any) => el?.id === id);

    return (
      <div>
        <div> Объект: {result?.object_name}</div>
        <div>Проект: {result?.calc_name}</div>
      </div>
    );
  };

  return (
    <FormProvider {...methods}>
      <Modal
        title='Новая таблица воздухообмена'
        open={openModal}
        onCancel={() => setOpenModal(false)}
        footer={null}
        closable={false}
      >
        <div className='my-6'>
          <div className='flex flex-col gap-4'>
            <InputForm
              name='name'
              label='Название'
              placeholder='Введите название таблицы'
            />
            <SelectForm
              placeholder='Выбрать проект'
              size='large'
              options={selectOptions}
              optionRender={({ data }) => {
                if (data.value !== 0) {
                  return <>{getSelectLabel(Number(data?.value))}</>;
                } else {
                  return (
                    <div className='text-primary'>
                      <PlusOutlined /> {data?.label}
                    </div>
                  );
                }
              }}
              name={`calculation_id`}
              onChange={handleSelectChange}
            />
          </div>
        </div>
        <div className='flex gap-2 '>
          <Button className='w-1/2' onClick={() => setOpenModal(false)}>
            Назад
          </Button>
          <Button
            className='w-1/2'
            disabled={!methods.formState.isValid || isSending}
            onClick={submit}
          >
            Создать
          </Button>
        </div>
      </Modal>
      <ModalNewProject
        open={openProjectModal}
        handleCancel={() => setOpenProjectModal(false)}
        setProjectId={(e: number) => methods.setValue('calculation_id', e)}
      />
    </FormProvider>
  );
};
